import { Box, Container, Grid, IconButton, TextField } from '@material-ui/core'
import { useEffect, useState } from 'react'
import { Upload } from '@progress/kendo-react-upload'
import { LocalizationProvider, loadMessages } from '@progress/kendo-react-intl'
import { ButtonHLPure, GridPure, HeaderHLPure } from '../../pures'
import { Dialog } from '@progress/kendo-react-dialogs';
import { useTranslation } from 'react-i18next';
import { Cancel } from '../../../assets/logos/icons'
import '../search-field.css'

const containerImageCustomStyles = {
    margin: 'auto',
    width: '18px',
    height: '18px',
}

const normalizeSearchValue = (value = '') => value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()

const ChatbotConfigurationKnowledgeView = (props) => {
    const {
        files,
        uploadFiles,
        filesToUpload,
        setFilesToUpload,
        fileToDelete,
        setFileToDelete,
        deleteFile
    } = props

    const { t, i18n } = useTranslation('chatbot', { keyPrefix: 'conversationsConfig' })
    const { t: tCommon } = useTranslation('common')
    const [messagesKendoUpload, setMessagesKendoUpload] = useState(null)
    const [locale, setLocale] = useState(null)
    const [query, setQuery] = useState('')

    const normalizedQuery = normalizeSearchValue(query.trim())
    const filteredFiles = normalizedQuery
        ? (files || []).filter((file) => normalizeSearchValue(file?.name).includes(normalizedQuery))
        : (files || [])

    const openFile = (file) => {
        window.open(file.path, "_blank", "noopener,noreferrer");
    };

    const onChange = (event) => {
        setFilesToUpload(event.newState)
    }

    /**
     * @description method to set labels accord language
     * @return promise
     */
    const handleChangeLabels = async () => await new Promise((resolve, reject) => {
        try {
            resolve(setMessagesKendoUpload({
                upload: {
                    cancel: t('knowledge.kendoUpload.cancel'),
                    clearSelectedFiles: t('knowledge.kendoUpload.clearSelectedFiles'),
                    dropFilesHere: t('knowledge.kendoUpload.dropFilesHere'),
                    headerStatusUploaded: t('knowledge.kendoUpload.headerStatusUploaded'),
                    headerStatusUploading: t('knowledge.kendoUpload.headerStatusUploading'),
                    invalidFileExtension: t('knowledge.kendoUpload.invalidFileExtension'),
                    invalidFiles: t('knowledge.kendoUpload.invalidFiles'),
                    invalidMaxFileSize: t('knowledge.kendoUpload.invalidMaxFileSize'),
                    invalidMinFileSize: t('knowledge.kendoUpload.invalidMinFileSize'),
                    remove: t('knowledge.kendoUpload.remove'),
                    retry: t('knowledge.kendoUpload.retry'),
                    select: t('knowledge.kendoUpload.select'),
                    uploadSelectedFiles: t('knowledge.kendoUpload.uploadSelectedFiles'),
                    total: t('knowledge.kendoUpload.total'),
                    files: t('knowledge.kendoUpload.files'),
                    selectTitle: t('knowledge.kendoUpload.selectTitle'),
                    selectNoFilesTitle: t('knowledge.kendoUpload.selectNoFilesTitle'),
                    statusUploaded: t('knowledge.kendoUpload.statusUploaded'),
                    statusUploadFailed: t('knowledge.kendoUpload.statusUploadFailed'),
                    dropZoneHint: t('knowledge.kendoUpload.dropZoneHint'),
                    defaultDropZoneNote: t('knowledge.kendoUpload.defaultDropZoneNote'),
                    dropZoneNote: t('knowledge.kendoUpload.dropZoneNote')
                }
            }))
        } catch (error) {
            reject(error)
        }
    })

    /**
     * @description effect to load labels in init render
     */
    useEffect(() => {
        handleChangeLabels()
    }, []);

    /**
     * @description effect to reload when language has changed
     */
    useEffect(() => {
       handleChangeLabels() 
    }, [t])

    /**
     * @description effect to set messages and locale language
     */
    useEffect(() => {
        try {
            if (messagesKendoUpload) {
                loadMessages(messagesKendoUpload, i18n.language)
                setLocale(i18n.language)
            }
        } catch (error) {
            console.error(error)
        }
    }, [messagesKendoUpload, locale])

    return (
        <>
            <Container maxWidth={'md'} className="kendoUpladoContainer">
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <p>{t("knowledge.uploadDescription")}</p>
                        {
                            (messagesKendoUpload && locale) &&
                            <LocalizationProvider language={locale}>
                                <Upload
                                    autoUpload={false}
                                    files={filesToUpload}
                                    withCredentials={false}
                                    multiple
                                    onAdd={onChange}
                                    onRemove={onChange}
                                    showActionButtons={false}
                                    restrictions={{
                                        allowedExtensions: [ 'txt', 'pdf', 'docx', 'xlsx', 'csv', 'json', 'md']
                                    }}
                                />
                            </LocalizationProvider>
                        }
                    </Grid>
                    {
                        filesToUpload.length > 0 &&
                        <Grid item xs={12} className='button-actions'>
                            <ButtonHLPure
                                onClick={() => uploadFiles(filesToUpload)}
                                nameBtn={t("knowledge.upload")}
                            />
                        </Grid>
                    }
                    <Grid item xs={12}>
                        <HeaderHLPure headingNumber={2} title={t("knowledge.uploadedDocuments")} />
                    </Grid>
                    <Grid item xs={12}>
                        <Box p={1} display='flex' alignItems='center'>
                            <TextField
                                className='project-search-field'
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                placeholder={tCommon("search") || 'Buscar documentos'}
                                variant='outlined'
                                size='small'
                                fullWidth
                                InputProps={{
                                    endAdornment: query && (
                                        <IconButton
                                            size='small'
                                            onClick={() => setQuery('')}
                                            style={{ padding: 4 }}
                                        >
                                            <img
                                                src={Cancel}
                                                alt=''
                                                style={containerImageCustomStyles}
                                            />
                                        </IconButton>
                                    ),
                                }}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <GridPure
                            key={`${normalizedQuery}-${filteredFiles.length}`}
                            dataGrid={filteredFiles}
                            skip={0}
                            take={6}
                            rowsItems={[
                                {
                                    field: 'name',
                                    title: tCommon("name"),
                                },
                                {
                                    field: 'Acciones',
                                    width: 80,
                                    actions: true
                                }
                            ]}
                            actions={[
                                {
                                    type: 'icon',
                                    value: 'k-icon k-i-download',
                                    onClick: openFile
                                },
                                {
                                    type: 'icon',
                                    value: 'k-icon k-i-delete k-i-trash',
                                    onClick: setFileToDelete
                                },
                            ]}
                        />
                        {
                            normalizedQuery && filteredFiles.length === 0 &&
                            <p>{t('knowledge.noResults') || 'No se encontraron documentos para la busqueda.'}</p>
                        }
                    </Grid>
                </Grid>
            </Container>
            {
                fileToDelete &&
                <Dialog
                    width={400}
                    onClose={() => setFileToDelete(false)}
                    title={tCommon("confirm")}
                >
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <p>{t("knowledge.confirmDeletion")} <strong>{fileToDelete.name}</strong>?</p>
                        </Grid>
                        <Grid item xs={12} className='button-actions'>
                            <ButtonHLPure colorScheme="error" outlined nameBtn={tCommon("cancel")} onClick={() => setFileToDelete(false)} />
                            <ButtonHLPure primary nameBtn={tCommon("accept")} onClick={deleteFile} />
                        </Grid>
                    </Grid>
                </Dialog>
            }
        </>
    )
}

export default ChatbotConfigurationKnowledgeView